import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Building2, Pencil, Trash2, MessageCircle, Plus } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { STAGES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { StageBadge, PriorityBadge } from "./Badges";
import { Sel } from "./Sel";
import { FollowUpSlot, FollowUpStepper, isDone } from "./FollowUpSlot";
import { LeadFormDialog } from "./LeadFormDialog";

const Info = ({ icon: I, children }) => (
  <div className="flex items-center gap-2 text-sm text-slate-600"><I className="h-4 w-4 text-slate-400" />{children || "-"}</div>
);

export const LeadDrawer = ({ leadId, onClose, onChanged }) => {
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [acts, setActs] = useState([]);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState(false);
  const canEdit = ["admin", "sales"].includes(user.role);

  useEffect(() => {
    if (!leadId) return setLead(null);
    api.get(`/leads/${leadId}`).then((r) => setLead(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/activities`, { params: { lead_id: leadId } }).then((r) => setActs(r.data));
  }, [leadId]);

  const updated = (d) => {
    setLead(d);
    onChanged?.();
    api.get(`/activities`, { params: { lead_id: leadId } }).then((r) => setActs(r.data));
  };

  const changeStage = async (s) => {
    try { updated((await api.patch(`/leads/${leadId}/status`, { status: s })).data); toast.success("Stage updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    updated((await api.post(`/leads/${leadId}/notes`, { text: note })).data);
    setNote("");
  };
  const addFollowup = async () => {
    updated((await api.post(`/leads/${leadId}/followups`)).data);
    toast.success("Follow-up slot added");
  };
  const del = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${leadId}`);
    toast.success("Lead deleted");
    onChanged?.();
    onClose();
  };

  const done = lead?.followups?.filter(isDone).length || 0;
  const totalFu = lead?.followups?.length || 5;

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-drawer">
        {!lead ? <div className="p-6 text-slate-500"><SheetTitle className="sr-only">Lead</SheetTitle>Loading...</div> : (
          <>
            <SheetHeader className="text-left">
              <div className="flex items-center gap-2"><StageBadge status={lead.status} testid="drawer-stage-badge" /><PriorityBadge p={lead.priority} /></div>
              <SheetTitle className="text-2xl font-extrabold tracking-tight" data-testid="drawer-lead-name">{lead.name}</SheetTitle>
              <div className="text-sm text-slate-500">{lead.company || "No company"}{lead.interested_for && <> · <span className="font-semibold text-emerald-700">{lead.interested_for}</span></>}{lead.state && ` · ${lead.state}`}</div>
            </SheetHeader>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {canEdit && <div className="w-44"><Sel testid="drawer-stage-select" value={lead.status} onChange={changeStage} options={STAGES.map((s) => ({ value: s.key, label: s.label }))} /></div>}
              {canEdit && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="drawer-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
              {lead.phone && <a href={`tel:${lead.phone}`}><Button variant="outline" size="sm" data-testid="drawer-call-btn"><Phone className="h-4 w-4 mr-1" />Call</Button></a>}
              {lead.phone && <a target="_blank" rel="noreferrer" href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}><Button variant="outline" size="sm" data-testid="drawer-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button></a>}
              {user.role === "admin" && <Button variant="outline" size="sm" className="text-rose-600" onClick={del} data-testid="drawer-delete-btn"><Trash2 className="h-4 w-4" /></Button>}
            </div>
            <Tabs defaultValue="followups" className="mt-6">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="overview" data-testid="drawer-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="followups" data-testid="drawer-tab-followups">Follow-ups ({done}/{totalFu})</TabsTrigger>
                <TabsTrigger value="notes" data-testid="drawer-tab-notes">Notes</TabsTrigger>
                <TabsTrigger value="activity" data-testid="drawer-tab-activity">Activity</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-3 pt-4">
                <Info icon={Building2}>{lead.company}</Info>
                <Info icon={Mail}>{lead.email}</Info>
                <Info icon={Phone}>{lead.phone}</Info>
                <Info icon={MapPin}>{lead.city}</Info>
                <div className="grid grid-cols-2 gap-3 pt-3 text-sm">
                  <div><div className="text-xs text-slate-500">State</div><div className="font-medium">{lead.state || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Interested for</div><div className="font-medium">{lead.interested_for || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Product / Service</div><div className="font-medium">{lead.service || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Source</div><div className="font-medium">{lead.source}</div></div>
                  <div><div className="text-xs text-slate-500">Assigned to</div><div className="font-medium">{lead.assigned_name || "Unassigned"}</div></div>
                  <div><div className="text-xs text-slate-500">Created</div><div className="font-medium">{fmtDate(lead.created_at)}</div></div>
                  <div><div className="text-xs text-slate-500">Created by</div><div className="font-medium">{lead.created_by_name}</div></div>
                </div>
                {lead.notes && <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{lead.notes}</div>}
              </TabsContent>
              <TabsContent value="followups" className="space-y-4 pt-4">
                <FollowUpStepper followups={lead.followups} />
                {lead.followups.map((fu) => <FollowUpSlot key={fu.no} leadId={lead.id} fu={fu} onSaved={updated} readOnly={!canEdit} />)}
                {canEdit && (
                  <Button variant="outline" className="w-full border-dashed" onClick={addFollowup} data-testid="add-followup-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add follow-up
                  </Button>
                )}
              </TabsContent>
              <TabsContent value="notes" className="space-y-3 pt-4">
                <Textarea data-testid="drawer-note-input" placeholder="Add an internal note..." value={note} onChange={(e) => setNote(e.target.value)} />
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={addNote} data-testid="drawer-add-note-btn">Add note</Button>
                {[...(lead.notes_log || [])].reverse().map((n, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`drawer-note-${i}`}>
                    <div>{n.text}</div><div className="mt-1 text-xs text-slate-400">{n.by} · {fmtDate(n.at)}</div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="activity" className="pt-4">
                <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                  {acts.map((a) => (
                    <li key={a.id} className="ml-4">
                      <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-emerald-500" />
                      <div className="text-sm font-medium">{a.details}</div>
                      <div className="text-xs text-slate-400">{a.user_name} · {fmtDate(a.created_at)}</div>
                    </li>
                  ))}
                  {!acts.length && <div className="ml-4 text-sm text-slate-500">No activity yet</div>}
                </ol>
              </TabsContent>
            </Tabs>
            <LeadFormDialog open={edit} onOpenChange={setEdit} lead={lead} onSaved={updated} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
